import {
  format,
  formatDistanceToNowStrict,
  parseISO,
  type Locale,
} from "date-fns";
import { de, enGB, es, ru, uk, fr, it, pt, sv, da } from "date-fns/locale";
import i18n from "@/i18n";

// Phase31: pick the date-fns locale matching the active i18n language.
// Fallback to English (UK variant for d MMMM-style closeness to other
// European locales) when the language code is unknown.
const _LOCALES: Record<string, Locale> = {
  en: enGB,
  ru,
  uk,
  es,
  de,
  fr,
  it,
  pt,
  sv,
  da,
};

function _locale(): Locale {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  return _LOCALES[lng] || enGB;
}

export function fmtDate(d: string | Date, pattern = "d MMM"): string {
  const date = typeof d === "string" ? parseISO(d) : d;
  return format(date, pattern, { locale: _locale() });
}

export function fmtLongDate(d: string | Date): string {
  return fmtDate(d, "d MMMM yyyy");
}

/** An athlete's calendar day, independent of the browser's timezone or UTC day. */
export function calendarDateInTimezone(timezone: string, now = new Date()): string {
  const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: timezone });
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    // Preserve saved preferences; an older browser or legacy invalid value must
    // not crash the entire plan page. Server-side validation owns future edits.
    formatter = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" });
  }
  const parts = formatter.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Date-only arithmetic: keep a calendar day out of local-time/DST conversion. */
export function addCalendarDays(iso: string, days: number): string {
  const value = new Date(`${iso}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function fmtWeekRange(start: string): string {
  const s = parseISO(start);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  const loc = _locale();
  return `${format(s, "d MMM", { locale: loc })} — ${format(e, "d MMM", { locale: loc })}`;
}

export function daysFromNow(d: string): number {
  const date = parseISO(d);
  const diff = date.getTime() - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

const _GREETINGS: Record<string, [string, string, string, string]> = {
  // [<5h, <12h, <18h, evening]
  en: ["Good night", "Good morning", "Good afternoon", "Good evening"],
  ru: ["Доброй ночи", "Доброе утро", "Добрый день", "Добрый вечер"],
  uk: ["Доброї ночі", "Доброго ранку", "Доброго дня", "Доброго вечора"],
  es: ["Buenas noches", "Buenos días", "Buenas tardes", "Buenas noches"],
  de: ["Gute Nacht", "Guten Morgen", "Guten Tag", "Guten Abend"],
  fr: ["Bonne nuit", "Bonjour", "Bon après-midi", "Bonsoir"],
  it: ["Buona notte", "Buongiorno", "Buon pomeriggio", "Buona sera"],
  pt: ["Boa noite", "Bom dia", "Boa tarde", "Boa noite"],
  sv: ["God natt", "God morgon", "God eftermiddag", "God kväll"],
  da: ["Godnat", "Godmorgen", "God eftermiddag", "Godaften"],
};

export function greetingForNow(): string {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  const opts = _GREETINGS[lng] || _GREETINGS.en;
  const h = new Date().getHours();
  if (h < 5) return opts[0];
  if (h < 12) return opts[1];
  if (h < 18) return opts[2];
  return opts[3];
}

export function relativeDistance(d: string): string {
  return formatDistanceToNowStrict(parseISO(d), {
    locale: _locale(),
    addSuffix: true,
  });
}
